import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Loader2, TrendingDown } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function ExpenseReportsContent() {
  const { activeBranchId } = useBranch();
  const { data, isLoading } = useQuery({
    queryKey: ["accounting-expense-reports", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/accounting/expense-reports${query}`, { credentials: "include" });
      return res.json();
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total Expenses (All time)</p>
            <p className="text-2xl font-bold text-destructive mt-1">{fmt(data?.totalExpenses ?? 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">This Month</p>
            <p className="text-2xl font-bold mt-1">{fmt(data?.thisMonthExpenses ?? 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Top Category</p>
            <p className="text-2xl font-bold mt-1 capitalize">{data?.topCategory ?? "—"}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Category Pie */}
        <Card>
          <CardHeader><CardTitle>Expenses by Category</CardTitle></CardHeader>
          <CardContent>
            {data?.categoryBreakdown?.length ? (
              <>
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.categoryBreakdown} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={90} paddingAngle={3}>
                        {data.categoryBreakdown.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v: number) => [fmt(v), ""]} contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-2">
                  {data.categoryBreakdown.slice(0, 6).map((c: any, i: number) => (
                    <div key={c.category} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span>{c.category}</span>
                      </div>
                      <span className="font-medium">{fmt(c.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No expense data</div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader><CardTitle>Monthly Expense Trend</CardTitle></CardHeader>
          <CardContent>
            {data?.monthlyTrend?.length ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => [fmt(v), "Expenses"]} contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }} />
                    <Bar dataKey="total" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} fillOpacity={0.8} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">No trend data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown Table */}
      <Card>
        <CardHeader><CardTitle>Category Details</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.categoryBreakdown?.map((c: any, i: number) => {
              const pct = data.totalExpenses > 0 ? ((c.total / data.totalExpenses) * 100).toFixed(1) : "0";
              return (
                <div key={c.category} className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{c.category}</span>
                      <span>{fmt(c.total)} <span className="text-muted-foreground">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {!data?.categoryBreakdown?.length && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No expenses recorded yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AccountingExpenseReports() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Expense Reports — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Deep-dive expense analytics for the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Expense Reports">
        <ExpenseReportsContent />
      </PlanGate>
    </div>
  );
}
